import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Mail, CheckCircle2, ArrowLeft, Loader2 } from "lucide-react";
import axios from "axios";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

/**
 * /unsubscribe?t=<token>
 *
 * Token-based, no login required. The link comes from the visible "Unsubscribe"
 * footer in marketing emails (trial drips + win-back). RFC 8058 one-click is
 * handled directly by the backend endpoint POST /api/email/unsubscribe — Gmail/
 * Apple Mail flip the flag without ever loading this page. This page exists
 * for captains who click the in-body link (most common path on mobile).
 */
export default function Unsubscribe() {
    const [params] = useSearchParams();
    const token = params.get("t") || "";

    const [status, setStatus] = useState("loading");  // loading | preview | done | error | resubscribed
    const [email, setEmail] = useState("");
    const [errMsg, setErrMsg] = useState("");

    // Step 1: preview — show the user what email is being unsubscribed.
    useEffect(() => {
        let cancelled = false;
        async function load() {
            if (!token) {
                setStatus("error");
                setErrMsg("This unsubscribe link is missing its token. If you got here by mistake, you're already fine — no changes were made.");
                return;
            }
            try {
                const { data } = await axios.get(`${API}/email/unsubscribe`, { params: { t: token } });
                if (cancelled) return;
                setEmail(data.email);
                setStatus(data.already_unsubscribed ? "done" : "preview");
            } catch (e) {
                if (cancelled) return;
                setStatus("error");
                setErrMsg(e.response?.data?.detail || "This unsubscribe link is invalid or has been tampered with.");
            }
        }
        load();
        return () => { cancelled = true; };
    }, [token]);

    const confirm = async () => {
        setStatus("loading");
        try {
            await axios.post(`${API}/email/unsubscribe`, null, { params: { t: token } });
            setStatus("done");
        } catch (e) {
            setStatus("error");
            setErrMsg(e.response?.data?.detail || "Couldn't process your unsubscribe. Try the link again in a moment.");
        }
    };

    const resub = async () => {
        setStatus("loading");
        try {
            await axios.post(`${API}/email/resubscribe`, null, { params: { t: token } });
            setStatus("resubscribed");
        } catch (e) {
            setStatus("error");
            setErrMsg(e.response?.data?.detail || "Couldn't re-subscribe you. Try the link again in a moment.");
        }
    };

    return (
        <div style={{
            minHeight: "100vh", background: "#040c18", color: "#e6edf6",
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: 24, fontFamily: "Arial, sans-serif",
        }}>
            <div style={{
                background: "#0a1828", borderRadius: 14, maxWidth: 520, width: "100%",
                border: "1px solid #1c3f63", overflow: "hidden",
                boxShadow: "0 18px 50px rgba(0,0,0,0.45)",
            }}>
                {/* Brand header */}
                <div style={{
                    background: "#081120", padding: "28px 24px", textAlign: "center",
                    fontFamily: "Georgia, serif",
                }}>
                    <div style={{ fontSize: 13, letterSpacing: "0.22em", textTransform: "uppercase", color: "#7c8aa3" }}>
                        Bosun&apos;s Mate
                    </div>
                    <div style={{ fontSize: 28, color: "#d4b572", fontStyle: "italic", marginTop: 6 }}>
                        Email preferences
                    </div>
                </div>

                <div style={{ padding: "30px 28px" }}>
                    {status === "loading" && (
                        <div data-testid="unsub-loading" style={{ textAlign: "center", padding: "24px 0", color: "#7c8aa3" }}>
                            <Loader2 size={20} className="bm-spin" style={{ marginBottom: 8 }} />
                            <div>Working on it…</div>
                        </div>
                    )}

                    {status === "preview" && (
                        <>
                            <div style={{ textAlign: "center", marginBottom: 18 }}>
                                <Mail size={28} color="#d4b572" />
                            </div>
                            <h2 className="bm-serif" style={{ fontSize: 22, fontStyle: "italic", color: "#d4b572", textAlign: "center", margin: "0 0 16px" }}>
                                Unsubscribe from updates?
                            </h2>
                            <p style={{ fontSize: 15, lineHeight: 1.55, color: "#cbd5e1" }}>
                                You&apos;ll no longer receive captain&apos;s updates (trial reminders, occasional product news, win-back offers) sent to
                                {" "}<strong data-testid="unsub-email" style={{ color: "#fff" }}>{email}</strong>.
                            </p>
                            <p style={{ fontSize: 13, lineHeight: 1.55, color: "#7c8aa3" }}>
                                Account-essential emails (password resets, payment receipts, co-captain invites) will still arrive — these are
                                transactional, not marketing.
                            </p>
                            <div style={{ display: "flex", gap: 10, marginTop: 22, flexWrap: "wrap" }}>
                                <button
                                    type="button"
                                    onClick={confirm}
                                    data-testid="unsub-confirm-btn"
                                    style={{
                                        flex: 1, padding: "12px 18px", borderRadius: 999,
                                        background: "#d4b572", color: "#1a1304", border: "none",
                                        cursor: "pointer", fontWeight: 600, fontSize: 14,
                                    }}
                                >
                                    Yes, unsubscribe
                                </button>
                                <a
                                    href="https://bosunsmate.app"
                                    style={{
                                        flex: 1, padding: "12px 18px", borderRadius: 999,
                                        background: "transparent", color: "#cbd5e1",
                                        border: "1px solid #1c3f63", textDecoration: "none",
                                        textAlign: "center", fontSize: 14,
                                    }}
                                >
                                    No, keep me on
                                </a>
                            </div>
                        </>
                    )}

                    {status === "done" && (
                        <>
                            <div style={{ textAlign: "center", marginBottom: 18 }}>
                                <CheckCircle2 size={32} color="#7ed3a8" />
                            </div>
                            <h2 className="bm-serif" style={{ fontSize: 22, fontStyle: "italic", color: "#d4b572", textAlign: "center", margin: "0 0 16px" }}>
                                You&apos;re unsubscribed
                            </h2>
                            <p style={{ fontSize: 15, lineHeight: 1.55, color: "#cbd5e1", textAlign: "center" }}>
                                <strong data-testid="unsub-email" style={{ color: "#fff" }}>{email}</strong> will no longer receive
                                captain&apos;s updates from Bosun&apos;s Mate. Fair winds.
                            </p>
                            <p style={{ fontSize: 13, lineHeight: 1.55, color: "#7c8aa3", textAlign: "center", marginTop: 16 }}>
                                Changed your mind?
                            </p>
                            <div style={{ display: "flex", justifyContent: "center", marginTop: 6 }}>
                                <button
                                    type="button"
                                    onClick={resub}
                                    data-testid="unsub-resub-btn"
                                    style={{
                                        padding: "8px 16px", borderRadius: 999,
                                        background: "transparent", color: "#5bb9e8",
                                        border: "1px solid #1c3f63", cursor: "pointer", fontSize: 13,
                                    }}
                                >
                                    Re-subscribe me
                                </button>
                            </div>
                        </>
                    )}

                    {status === "resubscribed" && (
                        <>
                            <div style={{ textAlign: "center", marginBottom: 18 }}>
                                <CheckCircle2 size={32} color="#7ed3a8" />
                            </div>
                            <h2 className="bm-serif" style={{ fontSize: 22, fontStyle: "italic", color: "#d4b572", textAlign: "center", margin: "0 0 16px" }}>
                                Welcome back aboard
                            </h2>
                            <p style={{ fontSize: 15, lineHeight: 1.55, color: "#cbd5e1", textAlign: "center" }}>
                                <strong data-testid="unsub-email" style={{ color: "#fff" }}>{email}</strong> is back on the captain&apos;s update list.
                            </p>
                        </>
                    )}

                    {status === "error" && (
                        <>
                            <h2 className="bm-serif" style={{ fontSize: 20, color: "#e76f51", textAlign: "center", margin: "0 0 14px" }}>
                                Hmm, that link didn&apos;t work
                            </h2>
                            <p data-testid="unsub-error" style={{ fontSize: 14, lineHeight: 1.55, color: "#cbd5e1", textAlign: "center" }}>
                                {errMsg}
                            </p>
                        </>
                    )}

                    <div style={{ textAlign: "center", marginTop: 26 }}>
                        <a
                            href="https://bosunsmate.app"
                            style={{ color: "#7c8aa3", fontSize: 13, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 4 }}
                        >
                            <ArrowLeft size={12} /> Back to bosunsmate.app
                        </a>
                    </div>
                </div>
            </div>
        </div>
    );
}
